import { useState } from 'react'
import { message, Modal } from 'antd'
import { useMutation } from '@apollo/client'

import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import { useValidateAuth } from '~/hooks/useValidateAuth'
import { graphQLErrorMessage } from '~/utils'

import {
  RECOMMEND_POST,
  CHECK_MUTE,
  DELETE_SQUARE_POST,
  COMPLAIN_TYPE_LIST,
  HIDE_SQUARE_POST,
  HIDE_COLLECTION,
} from '~/graphqls/gqls'
import {
  recommnedPost,
  recommnedPostVariables,
  ActionType,
  checkMute,
  checkMuteVariables,
  MuzzleTimeType,
  PostTypes,
  deleteSquarePost,
  deleteSquarePostVariables,
  getComplainTypeList,
  hideSquarePost,
  hideSquarePostVariables,
  hideCollection,
  hideCollectionVariables,
} from '~/graphqls/types'

export enum OPERATE_TYPE {
  /** 删除 */
  'DELETE' = 'DELETE',
  /** 禁言 */
  'MUTE' = 'MUTE',
  /** 隐藏 */
  'HIDDEN' = 'HIDDEN',
}
type Props = {
  post: PostTypes
  /** 成功回调 */
  onSuccess?: ({ key }: { key: OPERATE_TYPE }) => void
}
/** 白名单用户前台操作能力 */
export const useWhiteUserFrontControl = ({ post, onSuccess }: Props) => {
  const [isRecommended, setIsRecommended] = useState(post?.recommend || false)
  const [isMuted, setIsMuted] = useState(post?.user?.muzzleStatus || false)

  const { validateIsLogin } = useValidateAuth()
  const [apiRecommend, { loading: recommendLoading }] = useMutation<
    recommnedPost,
    recommnedPostVariables
  >(RECOMMEND_POST)
  const [apiMuteCheck, { loading: muteLoading }] = useMutation<
    checkMute,
    checkMuteVariables
  >(CHECK_MUTE)
  const [apiDelPost, { loading: delLoading }] = useMutation<
    deleteSquarePost,
    deleteSquarePostVariables
  >(DELETE_SQUARE_POST)
  const [apiHidePost, { loading: hideLoading }] = useMutation<
    hideSquarePost,
    hideSquarePostVariables
  >(HIDE_SQUARE_POST)
  // 白名单用户隐藏合集
  const [apiHideColleciton, { loading: hideCollectonLoading }] = useMutation<
    hideCollection,
    hideCollectionVariables
  >(HIDE_COLLECTION)

  /** 推荐帖子请求 */
  const handleRecommendRequest = async () => {
    try {
      const res = await apiRecommend({
        variables: {
          id: post?.id,
          actionType: isRecommended ? ActionType.CANCEL : ActionType.CONFIRM,
        },
      })
      if (res.data?.squareModule?.success) {
        message.destroy()
        message.success(`${isRecommended ? '移除' : '推荐'}成功`)
        setIsRecommended(!isRecommended)
      }
    } catch (error) {
      message.destroy()
      message.error(
        graphQLErrorMessage(error) || `${isRecommended ? '移除' : '推荐'}失败`
      )
    }
  }

  /** 推荐操作 */
  const handleRecommendClick = () => {
    if (!validateIsLogin()) return
    if (!post?.id) return
    if (recommendLoading) return
    if (isRecommended) {
      Modal.confirm({
        title: '提示',
        maskStyle: {
          backgroundColor: 'rgba(0,0,0,0.8)',
        },
        width: 270,
        className: 'hookModal dark',
        content: (
          <div className="text-neutral-400 text-sm text-center">
            确认将该作品移除推荐吗？
          </div>
        ),
        okText: '确认',
        onOk: handleRecommendRequest,
        cancelText: '取消',
        onCancel: close => {
          close()
        },
      })
    } else {
      setTimeout(() => {
        handleRecommendRequest()
      })
    }
  }

  /** 禁言请求 */
  const handleMuteRequest = async () => {
    try {
      const res = await apiMuteCheck({
        variables: {
          param: {
            uid: post?.user?.uid,
            enable: isMuted ? false : true,
            /** 禁言原因：有害信息 */
            reasonType: 1,
            /** 禁言时间 */
            timeType: MuzzleTimeType.SEVEN_DAYS,
            /** 禁言内容 */
            content: {
              bizId: post?.id,
              title: post?.title,
              image: post?.coverImage?.imageUrl,
            },
          },
        },
      })
      if (res.data?.success) {
        message.destroy()
        message.success(`${isMuted ? '取消禁言' : '禁言'}成功`)
        setIsMuted(!isMuted)
      }
    } catch (error) {
      message.destroy()
      message.error(
        graphQLErrorMessage(error) || `${isMuted ? '取消禁言' : '禁言'}失败`
      )
    }
  }

  /** 禁言操作 */
  const handleMuteClick = () => {
    if (!validateIsLogin()) return
    if (!post?.id) return
    if (muteLoading) return
    if (!isMuted) {
      Modal.confirm({
        title: '提示',
        maskStyle: {
          backgroundColor: 'rgba(0,0,0,0.8)',
        },
        width: 270,
        className: 'hookModal dark',
        content: (
          <div className="text-neutral-400 text-sm text-center">
            确认禁言该用户吗？
          </div>
        ),
        okText: '确认',
        onOk: handleMuteRequest,
        cancelText: '取消',
        onCancel: close => {
          close()
        },
      })
    } else {
      message.destroy()
      message.info('请在管理后台解除禁言')
    }
  }

  /** 删除帖子请求 */
  const handleDelPostRequest = async ({
    delReason,
  }: {
    /** 删除原因 */
    delReason: string
  }) => {
    try {
      const res = await apiDelPost({
        variables: {
          id: post?.id,
          remark: delReason,
        },
      })
      if (res.data?.squareModule?.success) {
        onSuccess?.({
          key: OPERATE_TYPE.DELETE,
        })
        message.destroy()
        message.success(`删除成功`)
      }
    } catch (error) {
      message.destroy()
      message.error(graphQLErrorMessage(error) || `删除失败`)
    }
  }

  /** 删除帖子操作 */
  const handleDelPostClick = ({
    delReason,
  }: {
    /** 删除原因 */
    delReason: string
  }) => {
    if (!validateIsLogin()) return
    if (!post?.id) return
    if (delLoading) return

    handleDelPostRequest({
      delReason,
    })
  }

  /** 隐藏帖子请求 */
  const handleHidePostRequest = async () => {
    try {
      const res = await apiHidePost({
        variables: {
          postId: post?.id,
        },
      })
      if (res.data?.squareModule?.success) {
        onSuccess?.({
          key: OPERATE_TYPE.HIDDEN,
        })
        message.destroy()
        message.success(`隐藏成功`)
      }
    } catch (error) {
      message.destroy()
      message.error(graphQLErrorMessage(error) || `隐藏失败`)
    }
  }

  /** 隐藏帖子操作 */
  const handleHidePostClick = () => {
    if (!validateIsLogin()) return
    if (!post?.id) return
    if (hideLoading) return

    Modal.confirm({
      title: '提示',
      maskStyle: {
        backgroundColor: 'rgba(0,0,0,0.8)',
      },
      width: 270,
      className: 'hookModal dark',
      content: (
        <div className="text-neutral-400 text-sm text-center">
          确定要隐藏 {post?.title} 吗？
        </div>
      ),
      okText: '确认',
      onOk: handleHidePostRequest,
      cancelText: '取消',
      onCancel: close => {
        close()
      },
    })
  }

  /** 隐藏合集请求 */
  const handleHideCollectionRequest = async () => {
    try {
      const res = await apiHideColleciton({
        variables: {
          id: post?.id,
        },
      })
      if (res.data?.squareModule?.success) {
        onSuccess?.({
          key: OPERATE_TYPE.DELETE,
        })
        message.destroy()
        message.success(`隐藏成功`)
      }
    } catch (error) {
      message.destroy()
      message.error(graphQLErrorMessage(error) || `隐藏失败`)
    }
  }

  const handleHideCollectionClick = () => {
    if (!validateIsLogin()) return
    if (!post?.id) return
    if (muteLoading) return
    Modal.confirm({
      title: '提示',
      maskStyle: {
        backgroundColor: 'rgba(0,0,0,0.8)',
      },
      width: 270,
      className: 'hookModal dark',
      content: (
        <div className="text-neutral-400 text-sm text-center">
          确定要隐藏 {post?.title} 吗？
          <div className="text-xs text-neutral-500">
            隐藏后将无法在【词/图】中查看该合集
          </div>
        </div>
      ),
      okText: '确认',
      onOk: handleHideCollectionRequest,
      cancelText: '取消',
      onCancel: close => {
        close()
      },
    })
  }

  return {
    /** 是否已推荐 */
    isRecommended,
    /** 推荐操作 */
    handleRecommendClick,
    /** 是否已禁言 */
    isMuted,
    /** 禁言操作 */
    handleMuteClick,
    /** 删除帖子操作 */
    handleDelPostClick,
    /** 删除loading */
    delLoading,
    /** 隐藏帖子操作 */
    handleHidePostClick,
    /** 隐藏合集 */
    handleHideCollectionClick,
    /** 隐藏合集loading */
    hideCollectonLoading,
  }
}

const REASON_SESSION_KEY = 'plaza_del_reason_list'
/** 后台白名单配置项目 */
export const useWhiteUserOptions = () => {
  // 删除帖子原因列表
  const [delReasonList, setDelReasonList] = useState<(string | null)[]>([])

  const fetchDelReasonList =
    useImperativeQuery<getComplainTypeList>(COMPLAIN_TYPE_LIST)

  /** 获取删除理由列表 */
  const getDelReasonList = async () => {
    // 利用session缓存
    const cacheReasonList = window.sessionStorage?.getItem(REASON_SESSION_KEY)
    if (cacheReasonList) {
      setDelReasonList(JSON.parse(cacheReasonList))
      return
    }
    try {
      const res = await fetchDelReasonList()
      const list = res?.data?.squareModule?.complaintTypeList

      if (!!list?.length) {
        window.sessionStorage?.setItem(REASON_SESSION_KEY, JSON.stringify(list))
        setDelReasonList(list)
      }
    } catch (error) {
      message.destroy()
      message.error(
        graphQLErrorMessage(error) || `获取删除原因配置失败，请稍后再试～`
      )
    }
  }

  return {
    /** 删除帖子原因列表 */
    delReasonList,
    /** 获取删除理由列表 */
    getDelReasonList,
  }
}
